import React, {
  createContext,
  useCallback,
  useState,
  ReactNode,
  useMemo,
  useContext,
} from "react";
import useUser from "../hooks/useUser";

interface OnboardingContextType {
  showDialog: null | "purchase" | "onboarding";
  onForceCloseOnboarding: () => void;
  onForceClosePayment: () => void;
}

export const OnboardingPopupContext = createContext<OnboardingContextType>({
  showDialog: null,
  onForceClosePayment: () => {},
  onForceCloseOnboarding: () => {},
});

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingPopupProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const [forceClosePayment, setForceClosePayment] = useState(false);
  const [forceCloseOnboarding, setForceCloseOnboarding] = useState(false);
  const user = useUser();

  let showDialog: null | "purchase" | "onboarding" = null;
  if (user && user?.student?.has_all_onboarding_data === false) {
    if (
      user.student.can_purchase_lessons &&
      !user.student.has_purchases &&
      !forceClosePayment
    ) {
      showDialog = "purchase";
    } else if (!forceCloseOnboarding) {
      showDialog = "onboarding";
    }
  }

  const onForceClosePayment = useCallback(() => {
    setForceClosePayment(true);
  }, []);

  const onForceCloseOnboarding = useCallback(() => {
    setForceCloseOnboarding(true);
  }, []);

  const providerValue = useMemo(
    () => ({
      showDialog,
      onForceCloseOnboarding,
      onForceClosePayment,
    }),
    [showDialog, onForceCloseOnboarding, onForceClosePayment]
  );

  return (
    <OnboardingPopupContext.Provider value={providerValue}>
      {children}
    </OnboardingPopupContext.Provider>
  );
};

export default function useOnboardingPopup() {
  const context = useContext(OnboardingPopupContext);
  if (!context) {
    throw new Error("Must be used within an OnboardingPopupProvider");
  }
  return context;
}
